import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  ScaleIcon,
  ChartBarIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { academyAPI } from '../../utils/api';
import UsageMeter from '../../components/student/UsageMeter';

const StatusBadge = ({ status }) => {
  const map = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    awaiting_student: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    awaiting_ai: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    abandoned: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${map[status] || map.abandoned}`}>
      {String(status || '').replace(/_/g, ' ')}
    </span>
  );
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [trials, setTrials] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [u, s, t, p] = await Promise.allSettled([
      academyAPI.getUsage(),
      academyAPI.getTutorSessions(),
      academyAPI.getTrials(),
      academyAPI.getProgress(),
    ]);
    if (u.status === 'fulfilled') setUsage(u.value.data);
    if (s.status === 'fulfilled') setSessions(s.value.data || []);
    if (t.status === 'fulfilled') setTrials(t.value.data || []);
    if (p.status === 'fulfilled') setProgress(p.value.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const greeting = user?.first_name ? `Welcome back, ${user.first_name}` : 'Welcome to EasyLaw Academy';

  const avgMastery = progress.length
    ? Math.round(progress.reduce((acc, p) => acc + (p.mastery || 0), 0) / progress.length)
    : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{greeting}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Your AI study companion for Zimbabwean law. A study aid, not legal advice.
        </p>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Link to="/student/study" className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-5 hover:shadow-md transition-shadow flex items-center">
          <BookOpenIcon className="h-8 w-8 text-primary-500 mr-4" />
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Study</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Tutor &amp; quizzes by topic</p>
          </div>
        </Link>
        <Link to="/student/mock-trials" className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-5 hover:shadow-md transition-shadow flex items-center">
          <ScaleIcon className="h-8 w-8 text-primary-500 mr-4" />
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Mock Trials</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Practice advocacy</p>
          </div>
        </Link>
        <Link to="/student/progress" className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-5 hover:shadow-md transition-shadow flex items-center">
          <ChartBarIcon className="h-8 w-8 text-primary-500 mr-4" />
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Progress</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Overall mastery {avgMastery}%</p>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: usage */}
        <div className="lg:col-span-1">
          <UsageMeter usage={usage} />
        </div>

        {/* Right: recent activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent tutor sessions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Recent study sessions</h3>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {sessions.length === 0 ? (
                <li className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                  No sessions yet. <Link to="/student/study" className="text-primary-600 dark:text-primary-400 hover:underline">Start studying</Link>.
                </li>
              ) : (
                sessions.slice(0, 5).map((s) => (
                  <li key={s.id} className="px-5 py-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.title || s.topic_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.topic_name}</p>
                    </div>
                    {s.topic && (
                      <Link to={`/student/study/${s.topic}`} className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0 ml-3">
                        Resume
                      </Link>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Recent trials */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center">
              <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Recent mock trials</h3>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {trials.length === 0 ? (
                <li className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                  No trials yet. <Link to="/student/mock-trials" className="text-primary-600 dark:text-primary-400 hover:underline">Start one</Link>.
                </li>
              ) : (
                trials.slice(0, 5).map((t) => (
                  <li key={t.id} className="px-5 py-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <Link to={`/student/mock-trials/${t.id}`} className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline truncate block">
                        {t.title || t.topic_name}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{t.topic_name}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      {typeof t.final_score === 'number' && (
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t.final_score}</span>
                      )}
                      <StatusBadge status={t.status} />
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
